import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { PageHeader, AdminCard, DataTable, Field, Input, Textarea, Btn, ConfirmDialog, PageLoader, datetimeFromIso } from "@/admin/components";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function ManualActions() {
  const [astros, setAstros] = useState([]);
  const [actions, setActions] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);

  const [create, setCreate] = useState({ full_name: "", email: "", city: "", specialisations: "" });
  const [override, setOverride] = useState({ astrologer_id: "", action: "activate", notes: "" });
  const [broadcast, setBroadcast] = useState({ audience: "all", city: "", subject: "", body: "" });
  const [confirmBroadcast, setConfirmBroadcast] = useState(false);

  const loadActions = useCallback(async (p) => {
    const { data } = await adminApi.get("/admin/actions", { params: { page: p } });
    setActions(data.actions);
    setTotal(data.total);
  }, []);

  useEffect(() => {
    adminApi.get("/admin/astrologers").then((r) => setAstros(r.data.astrologers));
    loadActions(page);
  }, [loadActions, page]);

  const run = async (fn, msg, after) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(typeof msg === "function" ? msg(r) : msg);
      after && after();
      loadActions(1);
      setPage(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async (kind) => {
    try {
      const res = await adminApi.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jk_${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const recipientCount = () => {
    if (broadcast.audience === "active") return astros.filter((a) => a.subscription_status === "active").length;
    if (broadcast.audience === "free") return astros.filter((a) => a.subscription_status === "free").length;
    if (broadcast.audience === "city") return astros.filter((a) => (a.city || "").toLowerCase() === broadcast.city.toLowerCase()).length;
    return astros.length;
  };

  const columns = [
    { key: "performed_at", label: "Timestamp", render: (r) => datetimeFromIso(r.performed_at) },
    { key: "action_type", label: "Action", render: (r) => <span className="font-medium">{r.action_type}</span> },
    { key: "target_type", label: "Target", render: (r) => r.target_type },
    { key: "notes", label: "Notes", render: (r) => <span className="text-[13px] text-[#666]">{r.notes || "—"}</span> },
  ];

  return (
    <div data-testid="admin-manual-actions-page">
      <PageHeader title="Manual Actions" subtitle="Operator tools · every action is audited" />

      <div className="grid grid-cols-2 gap-5">
        {/* Create astrologer */}
        <AdminCard className="p-6">
          <h3 className="text-[15px] font-semibold text-[#111]">Create Astrologer Account</h3>
          <p className="mb-4 mt-1 text-[13px] text-[#999]">Onboard a founding member or concierge signup.</p>
          <div className="space-y-3">
            <Field label="Full Name"><Input data-testid="create-name" value={create.full_name} onChange={(e) => setCreate({ ...create, full_name: e.target.value })} /></Field>
            <Field label="Email"><Input data-testid="create-email" value={create.email} onChange={(e) => setCreate({ ...create, email: e.target.value })} /></Field>
            <Field label="City"><Input value={create.city} onChange={(e) => setCreate({ ...create, city: e.target.value })} /></Field>
            <Field label="Specialisations (comma separated)"><Input value={create.specialisations} onChange={(e) => setCreate({ ...create, specialisations: e.target.value })} /></Field>
          </div>
          <div className="mt-4">
            <Btn testid="create-astro-btn" busy={busy} disabled={!create.full_name || !create.email}
              onClick={() => run(
                () => adminApi.post("/admin/manual/create-astrologer", {
                  full_name: create.full_name, email: create.email, city: create.city,
                  specialisations: create.specialisations.split(",").map((s) => s.trim()).filter(Boolean),
                }), "Astrologer created & welcome email sent",
                () => { setCreate({ full_name: "", email: "", city: "", specialisations: "" }); adminApi.get("/admin/astrologers").then((r) => setAstros(r.data.astrologers)); })}>
              Create Account
            </Btn>
          </div>
        </AdminCard>

        {/* Subscription override */}
        <AdminCard className="p-6">
          <h3 className="text-[15px] font-semibold text-[#111]">Override Subscription</h3>
          <p className="mb-4 mt-1 text-[13px] text-[#999]">Manually activate, deactivate or grant a lifetime plan.</p>
          <div className="space-y-3">
            <Field label="Astrologer">
              <select data-testid="override-astro" value={override.astrologer_id} onChange={(e) => setOverride({ ...override, astrologer_id: e.target.value })}
                className="h-11 w-full rounded-[10px] border border-[#e5e5e5] bg-white px-3 text-[14px] outline-none focus:border-[#111]">
                <option value="">Select astrologer…</option>
                {astros.map((a) => <option key={a.id} value={a.id}>{a.full_name} · {a.email}</option>)}
              </select>
            </Field>
            <Field label="Action">
              <select data-testid="override-action" value={override.action} onChange={(e) => setOverride({ ...override, action: e.target.value })}
                className="h-11 w-full rounded-[10px] border border-[#e5e5e5] bg-white px-3 text-[14px] outline-none focus:border-[#111]">
                <option value="activate">Activate</option>
                <option value="deactivate">Deactivate</option>
                <option value="lifetime">Set Lifetime</option>
              </select>
            </Field>
            <Field label="Notes (reason)"><Input value={override.notes} onChange={(e) => setOverride({ ...override, notes: e.target.value })} /></Field>
          </div>
          <div className="mt-4">
            <Btn testid="override-apply-btn" busy={busy} disabled={!override.astrologer_id}
              onClick={() => run(() => adminApi.post("/admin/manual/subscription-override", override), "Override applied",
                () => setOverride({ astrologer_id: "", action: "activate", notes: "" }))}>
              Apply Override
            </Btn>
          </div>
        </AdminCard>

        {/* Broadcast */}
        <AdminCard className="p-6">
          <h3 className="text-[15px] font-semibold text-[#111]">Broadcast Email</h3>
          <p className="mb-4 mt-1 text-[13px] text-[#999]">Email all astrologers or a filtered group.</p>
          <div className="space-y-3">
            <Field label="Audience">
              <select data-testid="broadcast-audience" value={broadcast.audience} onChange={(e) => setBroadcast({ ...broadcast, audience: e.target.value })}
                className="h-11 w-full rounded-[10px] border border-[#e5e5e5] bg-white px-3 text-[14px] outline-none focus:border-[#111]">
                <option value="all">All astrologers</option>
                <option value="active">Active only</option>
                <option value="free">Free tier only</option>
                <option value="city">Specific city</option>
              </select>
            </Field>
            {broadcast.audience === "city" && <Field label="City"><Input value={broadcast.city} onChange={(e) => setBroadcast({ ...broadcast, city: e.target.value })} /></Field>}
            <Field label="Subject"><Input data-testid="broadcast-subject" value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} /></Field>
            <Field label="Message"><Textarea rows={4} data-testid="broadcast-body" value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} /></Field>
          </div>
          <div className="mt-4">
            <Btn testid="broadcast-send-btn" disabled={!broadcast.subject || !broadcast.body} onClick={() => setConfirmBroadcast(true)}>
              Send Broadcast
            </Btn>
          </div>
        </AdminCard>

        {/* Export */}
        <AdminCard className="p-6">
          <h3 className="text-[15px] font-semibold text-[#111]">Export Data</h3>
          <p className="mb-4 mt-1 text-[13px] text-[#999]">Download platform data as CSV.</p>
          <div className="grid grid-cols-2 gap-3">
            {[["astrologers", "Astrologers"], ["bookings", "Bookings"], ["revenue", "Revenue"], ["notifications", "Notification Logs"]].map(([kind, label]) => (
              <button key={kind} data-testid={`export-${kind}`} onClick={() => exportCsv(kind)}
                className="flex h-11 items-center justify-center gap-2 rounded-[12px] border border-[#111111] bg-white text-[13px] font-semibold text-[#111] hover:bg-[#faf8f5]">
                <Download size={15} /> {label}
              </button>
            ))}
          </div>
        </AdminCard>
      </div>

      {/* Audit log */}
      <AdminCard className="mt-6 overflow-hidden">
        <div className="border-b border-[#e9e6e1] px-5 py-4">
          <h3 className="text-[15px] font-semibold text-[#111]">Admin Action History</h3>
        </div>
        {actions ? <DataTable columns={columns} rows={actions} testid="audit-table" empty="No admin actions yet." /> : <PageLoader />}
        {total > 20 && (
          <div className="flex items-center justify-between border-t border-[#e9e6e1] px-5 py-3 text-[13px] text-[#666]">
            <span>Page {page} · {total} total</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="rounded-md px-3 py-1 hover:bg-[#f2f0ec] disabled:opacity-40">Prev</button>
              <button disabled={page * 20 >= total} onClick={() => setPage(page + 1)} className="rounded-md px-3 py-1 hover:bg-[#f2f0ec] disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </AdminCard>

      <ConfirmDialog
        open={confirmBroadcast}
        onClose={() => setConfirmBroadcast(false)}
        busy={busy}
        danger={false}
        title="Send broadcast?"
        message={`You are about to send an email to ${recipientCount()} astrologer(s). This cannot be undone.`}
        confirmLabel="Send Broadcast"
        onConfirm={() => run(() => adminApi.post("/admin/manual/broadcast", broadcast), (r) => `Broadcast sent to ${r.data.sent} astrologer(s)`,
          () => { setBroadcast({ audience: "all", city: "", subject: "", body: "" }); setConfirmBroadcast(false); })}
      />
    </div>
  );
}
